import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Download, Trash2 } from 'lucide-react';
import { ApiError } from '@/api/client';
import { AlertDialog } from '@/components/ui/AlertDialog';
import { formatDateTime } from '@/lib/dateFormat';
import {
  deleteAttachment,
  updateAttachment,
  getDownloadUrl,
  type AttachmentEntityType,
  type AttachmentListItem,
} from '@/api/attachments';
import { iconForContentType, formatBytes } from './fileIcons';

function localPart(value: string): string {
  return value.split('@')[0] ?? value;
}

interface AttachmentItemProps {
  attachment: AttachmentListItem;
  entityType: AttachmentEntityType;
  entityId: number;
}

/**
 * One attachment row: type icon, filename (click to download), size, uploader,
 * date, an optional description, and — for writers — edit-description + delete.
 * Download mints a fresh SAS URL then triggers a temporary anchor click so the
 * SAS token never appears in the address bar.
 */
export function AttachmentItem({ attachment, entityType, entityId }: AttachmentItemProps) {
  const queryClient = useQueryClient();
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [downloading, setDownloading] = useState(false);
  const [editingDesc, setEditingDesc] = useState(false);
  const [descDraft, setDescDraft] = useState('');

  const Icon = iconForContentType(attachment.ContentType);

  const download = async () => {
    setError(null);
    setDownloading(true);
    try {
      const { DownloadUrl, FileName } = await getDownloadUrl(attachment.AttachmentId);
      const a = document.createElement('a');
      a.href = DownloadUrl;
      a.download = FileName;
      a.target = '_blank';
      a.rel = 'noopener';
      document.body.appendChild(a);
      a.click();
      a.remove();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to start the download.');
    } finally {
      setDownloading(false);
    }
  };

  const startEditDesc = () => { setError(null); setDescDraft(attachment.Description ?? ''); setEditingDesc(true); };
  const descMutation = useMutation({
    mutationFn: () => updateAttachment(attachment.AttachmentId, { Description: descDraft.trim() || null }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['attachments', { entityType, entityId }] });
      setEditingDesc(false);
    },
    onError: (err) => setError(err instanceof ApiError ? err.message : 'Failed to save the description.'),
  });

  const deleteMutation = useMutation({
    mutationFn: () => deleteAttachment(attachment.AttachmentId),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['attachments', { entityType, entityId }] });
    },
    onError: (err) => setError(err instanceof ApiError ? err.message : 'Failed to delete the attachment.'),
  });

  return (
    <div className="group flex items-center gap-3 rounded-md border border-border px-3 py-2">
      <Icon size={18} aria-hidden className="shrink-0 text-darkGrey/50" />

      <div className="min-w-0 flex-1">
        <button
          type="button"
          onClick={download}
          disabled={downloading}
          className="block max-w-full truncate text-left text-sm font-semibold text-navy hover:underline disabled:opacity-60"
          title={attachment.FileName}
        >
          {attachment.FileName}
        </button>
        <p className="text-[0.7rem] text-darkGrey/60">
          {formatBytes(attachment.SizeBytes)} · {localPart(attachment.UploadedBy)} · {formatDateTime(attachment.UploadedDate)}
        </p>

        {/* Description / caption — editable by any writer (CanDelete). */}
        {editingDesc ? (
          <div className="mt-1 flex items-center gap-1.5">
            <input
              autoFocus
              value={descDraft}
              onChange={(e) => setDescDraft(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') descMutation.mutate(); if (e.key === 'Escape') setEditingDesc(false); }}
              maxLength={500}
              placeholder="Describe this file…"
              aria-label={`Description for ${attachment.FileName}`}
              className="min-w-0 flex-1 rounded border border-border px-2 py-0.5 text-xs focus:border-navy focus:outline-none focus:ring-1 focus:ring-navy"
            />
            <button type="button" onClick={() => descMutation.mutate()} disabled={descMutation.isPending} className="text-xs font-semibold text-orange hover:underline disabled:opacity-60">Save</button>
            <button type="button" onClick={() => setEditingDesc(false)} className="text-xs font-semibold text-darkGrey/60 hover:text-navy">Cancel</button>
          </div>
        ) : attachment.Description ? (
          <p className="mt-0.5 text-xs text-darkGrey/80">
            {attachment.Description}
            {attachment.CanDelete && <button type="button" onClick={startEditDesc} className="ml-2 text-darkGrey/40 hover:text-navy">Edit</button>}
          </p>
        ) : attachment.CanDelete ? (
          <button type="button" onClick={startEditDesc} className="mt-0.5 text-xs font-semibold text-darkGrey/40 hover:text-navy">+ Add description</button>
        ) : null}

        {error && <p className="mt-0.5 text-xs text-red" role="alert">{error}</p>}
      </div>

      <div className="flex items-center gap-1">
        <button
          type="button"
          onClick={download}
          disabled={downloading}
          aria-label={`Download ${attachment.FileName}`}
          className="rounded p-1 text-darkGrey/40 hover:text-navy disabled:opacity-60"
        >
          <Download size={15} aria-hidden />
        </button>
        {attachment.CanDelete && (
          <button
            type="button"
            onClick={() => { setError(null); setConfirmDelete(true); }}
            aria-label={`Delete ${attachment.FileName}`}
            className="rounded p-1 text-darkGrey/40 opacity-0 transition-opacity group-hover:opacity-100 focus:opacity-100 hover:text-red"
          >
            <Trash2 size={15} aria-hidden />
          </button>
        )}
      </div>

      <AlertDialog
        open={confirmDelete}
        onOpenChange={setConfirmDelete}
        title="Delete this file?"
        description="This removes the file from the contract's Files list. The stored copy is retained (soft delete) and can be restored by an administrator."
        confirmLabel="Delete file"
        destructive
        onConfirm={() => deleteMutation.mutate()}
      />
    </div>
  );
}

export default AttachmentItem;
